import {useMutation, useQuery} from '@tanstack/react-query';

import {
  RQ_DELETE_USER,
  RQ_FIND_USER_BY_ID,
  RQ_FORGOT_PASSWORD_API,
  RQ_RESET_PASSWORD_API,
  RQ_UPDATE_USER,
  RQ_USER_LOGIN_API,
  RQ_USER_LOOKUP_ALL_ROLE,
  RQ_USER_REGISTER_ROLE,
  RQ_USER_REGISTRATION_API,
} from './Constants';
import {rqCreateRequest} from './rqHttpUtils';

const withUrl = (config, key, value) => ({
  ...config,
  url: config.url.replace(`<%= ${key} %>`, value),
});

export const RQUserLoginAPI = (options = {}) =>
  useMutation({
    mutationKey: ['rq_login_user'],
    mutationFn: async formData => {
      if (!formData) {
        throw new Error('formData is undefined');
      }
      return rqCreateRequest(RQ_USER_LOGIN_API, formData);
    },
    ...options,
  });

export const RQUserRegistrationAPI = (options = {}) =>
  useMutation({
    mutationKey: ['rq_register_user'],
    mutationFn: async formData => {
      if (!formData) {
        throw new Error('formData is undefined');
      }
      return rqCreateRequest(RQ_USER_REGISTRATION_API, formData);
    },
    ...options,
  });

export const RQUserRegistrationRole = (role, options = {}) =>
  useMutation({
    mutationKey: ['rq_user_registration', role],
    mutationFn: formData =>
      rqCreateRequest(
        withUrl(RQ_USER_REGISTER_ROLE, 'role', role),
        formData,
      ),
    ...options,
  });

export const RqUserLookupAllRole = (role, token, options = {}) =>
  useQuery({
    queryKey: ['rq_user_lookup_all_role', role],
    queryFn: () =>
      rqCreateRequest(
        withUrl(RQ_USER_LOOKUP_ALL_ROLE, 'role', role),
        null,
        token,
        null,
      ).then(res => res ?? []),
    ...options,
    enabled: !!role && !!token && (options.enabled ?? true),
  });

export const RqUserLookupAll = (token, options = {}) =>
  useQuery({
    queryKey: ['rq_user_lookup_all'],
    queryFn: async () => {
      const roles = [
        'ADMIN',
        'DOCTOR',
        'THERAPIST',
        'ACCOUNTANT',
        'PHARMACIST',
        'RECEPTIONIST',
      ];

      const responses = await Promise.all(
        roles.map(role =>
          rqCreateRequest(
            withUrl(RQ_USER_LOOKUP_ALL_ROLE, 'role', role),
            null,
            token,
            null,
          ).then(res => (Array.isArray(res) ? res : [])),
        ),
      );

      return responses.flat();
    },
    enabled: !!token,
    ...options,
  });

export const RQForgotPasswordAPI = () =>
  useMutation({
    mutationFn: async ({email}) =>
      rqCreateRequest(
        RQ_FORGOT_PASSWORD_API,
        null,
        null,
        null,
        null,
        'application/json',
        'application/json',
        {email},
      ),
  });

export const RQResetPasswordAPI = (options = {}) =>
  useMutation({
    mutationKey: ['rq_reset_password'],
    mutationFn: async ({formData}) =>
      rqCreateRequest(
        RQ_RESET_PASSWORD_API,
        formData,
        null,
        null,
      ),
    ...options,
  });

export const RqUserLookupById = (id, token, options = {}) =>
  useQuery({
    queryKey: ['rq_user_lookup_by_id', id],
    queryFn: () =>
      rqCreateRequest(
        withUrl(RQ_FIND_USER_BY_ID, 'id', id),
        null,
        token,
        null,
      ).then(res => res ?? null),
    ...options,
    enabled: !!id && !!token && (options.enabled ?? true),
  });

export const RQUpdateUser = (options = {}) =>
  useMutation({
    mutationKey: ['rq_update_user'],
    mutationFn: ({id, formData, token}) =>
      rqCreateRequest(
        withUrl(RQ_UPDATE_USER, 'id', id),
        formData,
        token,
        null,
      ),
    ...options,
  });

export const RqDeleteUser = (callbacks = {}) =>
  useMutation({
    mutationKey: ['rq_delete_user'],
    mutationFn: ({id, token}) =>
      rqCreateRequest(
        withUrl(RQ_DELETE_USER, 'id', id),
        null,
        token,
        null,
      ),
    ...callbacks,
  });
